import { z } from "zod";
import { readChargeCodeApi } from "@/config/charge-code-api/charge-code-api.rules";

/**
 * Environment configuration schema with validation
 */
const envSchema = z.object({
  // Application environment
  env: z.enum(["development", "test", "staging", "production"]).default("development"),

  // Permission catalogue in force: v1 = hard-coded role tables, v2 = named permissions
  permissionCatalogue: z.enum(["v1", "v2"]).default("v1"),

  // Charge-code API this UI talks to. The server serves v1 and v2 side by side; this selects one.
  chargeCodeApi: z.enum(["v1", "v2"]),

  // API Configuration
  apiUrl: z.string().url("Invalid API URL"),
  medispanIconBaseUrl: z.string().url("Invalid Medi-Span icon base URL"),
  apiUrlOrganization: z.string().url("Invalid organization API URL").optional(),
  apiUrlPartner: z.string().url("Invalid partner API URL").optional(),
  apiUrlDocuments: z.string().url("Invalid documents API URL").optional(),
  apiUrlAppointments: z.string().url("Invalid appointments API URL").optional(),
  // The API Gateway route in front of support-bot; the dev one when unset
  apiUrlAssistant: z.string().url("Invalid support-bot API URL").optional(),
  apiUrlBlockTimeSlots: z.string().url("Invalid block time slots API URL").optional(),
  apiUrlApptSmsReminder: z.string().url("Invalid appt sms reminder API URL").optional(),
  apiUrlApptSmsReminderTrigger: z
    .string()
    .url("Invalid appt sms reminder trigger API URL")
    .optional(),
  // Security Configuration
  encryptionSecretKey: z.string().min(32, "Encryption secret key must be at least 32 characters"),
  encryptionSecretIV: z.string().min(16, "Encryption IV must be at least 16 characters"),

  // WebSocket Configuration
  websocketUrl: z.string().url("Invalid websocket URL").optional(),
  sessionWebsocketUrl: z.string().url("Invalid session websocket URL").optional(),
  notificationWebsocketUrl: z.string().url("Invalid notification websocket URL").optional(),

  // Authentication Configuration
  clientId: z.string().min(1, "Client ID is required"),
  cognitoDomain: z.string().url("Invalid Cognito domain URL"),
  cognitoLogoutUrl: z.string().url("Invalid Cognito logout URL"),
  tokenExchangeUrl: z.string().url("Invalid token exchange URL"),
  tpassLoginUrl: z.string().url("Invalid TPass login URL"),
  userPoolId: z.string().min(1, "User pool ID is required"),

  apiKeyOrganization: z.string().min(1, "API key organization is required"),

  apiKeyAppointments: z.string().min(1, "API key appointments is required").optional(),
  apiKeyBlockTimeSlots: z.string().min(1, "API key block time slots is required").optional(),
  // Local Service URLs (only for development)
});

/**
 * Parse and validate environment variables
 */
function parseEnvVariables() {
  const rawEnv = {
    env: import.meta.env.VITE_ENV,
    permissionCatalogue: import.meta.env.VITE_PERMISSION_CATALOGUE,
    chargeCodeApi: readChargeCodeApi(import.meta.env.VITE_CHARGE_CODE_API),
    apiUrl: import.meta.env.VITE_API_URL,
    medispanIconBaseUrl: import.meta.env.VITE_MEDISPAN_ICON_BASE_URL,
    apiUrlOrganization: import.meta.env.VITE_API_URL_ORGANIZATION,
    apiUrlPartner: import.meta.env.VITE_API_URL_PARTNER,
    apiUrlDocuments: import.meta.env.VITE_API_URL_DOCUMENTS,
    apiUrlAppointments: import.meta.env.VITE_API_URL_APPOINTMENTS,
    apiUrlAssistant: import.meta.env.VITE_API_URL_ASSISTANT,
    apiUrlBlockTimeSlots: import.meta.env.VITE_API_URL_APPOINTMENTS,
    apiUrlApptSmsReminder: import.meta.env.VITE_API_URL_APPOINTMENTS,
    apiUrlApptSmsReminderTrigger: import.meta.env.VITE_API_URL_APPOINTMENTS,
    encryptionSecretKey: import.meta.env.VITE_ENCRYPTION_SECRET_KEY,
    encryptionSecretIV: import.meta.env.VITE_ENCRYPTION_SECRET_IV,

    websocketUrl: import.meta.env.VITE_WEBSOCKET_URL,
    sessionWebsocketUrl: import.meta.env.VITE_SESSION_WEBSOCKET_URL,
    notificationWebsocketUrl: import.meta.env.VITE_NOTIFICATION_WEBSOCKET_URL,
    clientId: import.meta.env.VITE_CLIENT_ID,
    cognitoDomain: import.meta.env.VITE_COGNITO_DOMAIN,
    cognitoLogoutUrl: import.meta.env.VITE_COGNITO_LOGOUT_URL,
    tokenExchangeUrl: import.meta.env.VITE_TOKEN_EXCHANGE_URL,
    tpassLoginUrl: import.meta.env.VITE_TPASS_LOGIN_URL,
    userPoolId: import.meta.env.VITE_USER_POOL_ID,

    hmsUrl: import.meta.env.VITE_HMS_URL,

    apiKeyOrganization: import.meta.env.VITE_X_API_KEY_ORGANIZATION,
    apiKeyAppointments: import.meta.env.VITE_X_API_KEY_APPOINTMENTS,
    apiKeyBlockTimeSlots: import.meta.env.VITE_X_API_KEY_BLOCK_TIME_SLOTS,
  };

  try {
    return envSchema.parse(rawEnv);
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errorMessages = error.errors.map((err) => `${err.path.join(".")}: ${err.message}`);
      throw new Error(`Environment validation failed:\n${errorMessages.join("\n")}`);
    }
    throw error;
  }
}

/**
 * Validated environment variables
 */
export const EnvVariables = parseEnvVariables();

/**
 * Type for environment variables
 */
export type EnvVariables = z.infer<typeof envSchema>;

/**
 * Check if current environment is development
 */
export const isDevelopment = () => EnvVariables.env === "development";

/**
 * Check if current environment is production
 */
export const isProduction = () => EnvVariables.env === "production";

/**
 * Check if current environment is test
 */
export const isTest = () => EnvVariables.env === "test";

/**
 * Check if current environment is staging
 */
export const isStaging = () => EnvVariables.env === "staging";

/**
 * Permission catalogue currently in force.
 *
 * `v1` keeps the hard-coded role tables in `config/permission/permission.config.ts`.
 * `v2` checks the permission names granted to the role selected at `/additional-info`.
 */
export const getPermissionCatalogue = (): "v1" | "v2" => EnvVariables.permissionCatalogue;

/**
 * Whether the v2 permission catalogue is in force.
 */
export const isPermissionCatalogueV2 = (): boolean => getPermissionCatalogue() === "v2";
